import React, { useEffect, useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GRUPO_AUTORIZADO } from './authConfig';
import { AdminContext, useAdmin } from './context/AdminContext';
import { RRHHContext, useRRHH } from './context/RRHHContext';
import Login from './Login';
import Header from './components/Header';
import AvisoNuevaVersion from './components/AvisoNuevaVersion';
import NavBar from './components/NavBar';
import Hub from './pages/Hub';
import Consultas from './pages/Consultas';
import Proveedores from './pages/Proveedores';
import Cotizaciones from './pages/Cotizaciones';
import Clientes from './pages/Clientes';
import Facturas from './pages/Facturas';
import PagosProveedor from './pages/PagosProveedor';
import OfertasCliente from './pages/OfertasCliente';
import Proyectos from './pages/Proyectos';
import CobrosCliente from './pages/CobrosCliente';
import PagosCliente from './pages/PagosCliente';
import Proyeccion from './pages/Proyeccion';
import ProyectosPorCerrar from './pages/ProyectosPorCerrar';
import Configuracion from './pages/Configuracion';
import Gastos from './pages/Gastos';
import GastosRecurrentes from './pages/GastosRecurrentes';
import GastosAprobacion from './pages/GastosAprobacion';
import GastosCatalogos from './pages/GastosCatalogos';
import RrhhExpedientes from './pages/RrhhExpedientes';
import RrhhExpedienteDetalle from './pages/RrhhExpedienteDetalle';
import RrhhAcciones from './pages/RrhhAcciones';
import RrhhCatalogos from './pages/RrhhCatalogos';
import './App.css';

// ── IDs de grupos Azure AD ───────────────────────────────────────────
const GRUPO_ADMINS = '6bd7bee9-a05d-4ca0-a01f-48a13657ae70';
// ADENDA 1 v49a: grupo RRHH, independiente de admin. GUID del grupo "JYA RRHH"
// (Azure AD, creado 28-jul-2026). Vacío => esRRHH false (falla en seguro).
const GRUPO_RRHH = 'cab18f91-f5cf-440c-b6e6-19d949dbf1fb';

// ── Loading Screen ───────────────────────────────────────────────────
function LoadingScreen({ mensaje = 'Iniciando sistema...' }) {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(iv);
  }, []);
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', background: '#012E40',
      fontFamily: 'Segoe UI, sans-serif', gap: 24
    }}>
      <img src="/logo-blanco.png" alt="Logo" style={{ width: 180, marginBottom: 8 }} />
      <div style={{
        width: 48, height: 48, border: '4px solid rgba(135,183,73,0.3)',
        borderTop: '4px solid #87B749', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
        {mensaje}{dots}
      </div>
    </div>
  );
}

// ── Acceso Denegado ──────────────────────────────────────────────────
function AccesoDenegado() {
  const { instance } = useMsal();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100vh', background: '#012E40',
      fontFamily: 'Segoe UI, sans-serif', gap: 16
    }}>
      <img src="/logo-blanco.png" alt="Logo" style={{ width: 160, marginBottom: 8 }} />
      <div style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>Acceso restringido</div>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, textAlign: 'center', maxWidth: 320 }}>
        Tu cuenta no tiene acceso al Sistema Administrativo JYA.
        Contacta al administrador para solicitar acceso.
      </p>
      <button onClick={() => instance.logoutRedirect()} style={{
        padding: '10px 24px', background: '#87B749', color: '#fff',
        border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer',
        fontWeight: '600'
      }}>
        Cerrar sesión
      </button>
    </div>
  );
}

// ── Guard de admin para rutas restringidas (D16) ─────────────────────
function RequireAdmin({ children }) {
  const isAdmin = useAdmin();
  return isAdmin ? children : <Navigate to="/app/inicio" replace />;
}

// ── Guard de RRHH (ADENDA 1 v49a) — hermano de RequireAdmin, para /app/rrhh/* ──
function RequireRRHH({ children }) {
  const esRRHH = useRRHH();
  return esRRHH ? children : <Navigate to="/app/inicio" replace />;
}

// ── App principal ────────────────────────────────────────────────────
function App() {
  const { accounts, inProgress } = useMsal();

  if (inProgress !== 'none') {
    return <LoadingScreen mensaje="Autenticando" />;
  }

  const grupos = accounts[0]?.idTokenClaims?.groups || [];
  const tieneAcceso = grupos.includes(GRUPO_AUTORIZADO);
  const esAdmin     = grupos.includes(GRUPO_ADMINS);
  const esRRHH      = !!GRUPO_RRHH && grupos.includes(GRUPO_RRHH);   // ADENDA 1

  return (
    <AdminContext.Provider value={esAdmin}>
      <RRHHContext.Provider value={esRRHH}>
      <AuthenticatedTemplate>
        {tieneAcceso ? (
          <Router>
            {/* Layout sin ramificacion (D18): Header global arriba +
                fila (NavBar contextual auto-ocultable + contenido). */}
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
              <AvisoNuevaVersion />
              <Header />
              <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <NavBar />
                <div style={{ flex: 1, background: '#f5f6f8', overflowY: 'auto' }}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/app/inicio" replace />} />
                    <Route path="/app/inicio" element={<Hub />} />
                    <Route path="/app/consultas" element={<Consultas />} />

                    {/* Cuentas por Pagar */}
                    <Route path="/app/cxp/proveedores" element={<Proveedores />} />
                    <Route path="/app/cxp/cotizaciones-proveedor" element={<Cotizaciones />} />
                    <Route path="/app/cxp/facturas-proveedor" element={<Facturas />} />
                    <Route path="/app/cxp/pagos-proveedor" element={<PagosProveedor />} />

                    {/* Cuentas por Cobrar */}
                    <Route path="/app/cxc/clientes" element={<Clientes />} />
                    <Route path="/app/cxc/ofertas-cliente" element={<OfertasCliente />} />
                    <Route path="/app/cxc/proyectos" element={<Proyectos />} />
                    <Route path="/app/cxc/cobros-cliente" element={<CobrosCliente />} />
                    <Route path="/app/cxc/pagos-cliente" element={<PagosCliente />} />
                    <Route path="/app/cxc/proyeccion-facturacion" element={<Proyeccion />} />
                    <Route path="/app/cxc/proyectos-por-cerrar" element={
                      <RequireAdmin><ProyectosPorCerrar /></RequireAdmin>
                    } />

                    {/* Gastos Operativos (v38.0) */}
                    <Route path="/app/gastos/registro" element={<Gastos />} />
                    <Route path="/app/gastos/recurrentes" element={<GastosRecurrentes />} />
                    <Route path="/app/gastos/aprobacion" element={<GastosAprobacion />} />
                    <Route path="/app/gastos/catalogos" element={
                      <RequireAdmin><GastosCatalogos /></RequireAdmin>
                    } />

                    {/* Recursos Humanos (v49) - guard por grupo RRHH, NO admin (D16) */}
                    <Route path="/app/rrhh/expedientes" element={
                      <RequireRRHH><RrhhExpedientes /></RequireRRHH>
                    } />
                    <Route path="/app/rrhh/expedientes/:id" element={
                      <RequireRRHH><RrhhExpedienteDetalle /></RequireRRHH>
                    } />
                    <Route path="/app/rrhh/acciones" element={
                      <RequireRRHH><RrhhAcciones /></RequireRRHH>
                    } />
                    <Route path="/app/rrhh/catalogos" element={
                      <RequireRRHH><RrhhCatalogos /></RequireRRHH>
                    } />

                    {/* Configuracion (guard admin - D16) */}
                    <Route path="/app/configuracion" element={
                      <RequireAdmin><Configuracion /></RequireAdmin>
                    } />

                    {/* Catch-all -> Hub (D9) */}
                    <Route path="*" element={<Navigate to="/app/inicio" replace />} />
                  </Routes>
                </div>
              </div>
            </div>
          </Router>
        ) : (
          <AccesoDenegado />
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
      </RRHHContext.Provider>
    </AdminContext.Provider>
  );
}

export default App;
